import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/hooks/use-toast";
import { api } from "@/api";
import { formatError } from "@/components/shared/helpers";
import {
  CalendarRange,
  Trash2,
  PlusCircle,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Sparkles,
} from "lucide-react";

const SUGGESTED_PLATFORMS = ["Airbnb", "Booking", "Abritel", "VRBO", "Gîtes de France", "Autre"];

// Messages d'aide explicites selon le type d'erreur retourné par le backend
const SYNC_ERROR_MESSAGES = {
  http_401: "⚠️ Accès refusé par la plateforme. Vérifiez que votre lien iCal est bien le lien public fourni par la plateforme (pas une page de connexion). Sur Abritel/VRBO : Tableau de bord → Calendrier → Exporter → Copier le lien iCal. Sur Airbnb : Calendrier → Disponibilités → Lien d'exportation. Sur Booking : Extranet → Calendrier → Synchroniser → iCal. Si le problème persiste, contactez Blanc des Montagnes.",
  http_403: "⚠️ Accès refusé par la plateforme. Vérifiez que votre lien iCal est bien le lien public fourni par la plateforme (pas une page de connexion). Sur Abritel/VRBO : Tableau de bord → Calendrier → Exporter → Copier le lien iCal. Sur Airbnb : Calendrier → Disponibilités → Lien d'exportation. Sur Booking : Extranet → Calendrier → Synchroniser → iCal. Si le problème persiste, contactez Blanc des Montagnes.",
  http_404: "⚠️ Lien introuvable (404). Le calendrier n'existe plus ou l'URL a changé. Récupérez à nouveau le lien depuis votre plateforme.",
  fetch_failed: "⚠️ Connexion impossible. Vérifiez votre lien iCal et réessayez.",
  invalid_ical: "⚠️ Le lien ne pointe pas vers un calendrier valide.",
};

function explainSyncError(errCode) {
  if (!errCode) return null;
  return SYNC_ERROR_MESSAGES[errCode] || `⚠️ Erreur de synchronisation (${errCode}). Vérifiez votre lien iCal.`;
}

function genId() {
  return `ical-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function IcalSourceRow({ source, properties, onChange, onRemove }) {
  const [showCustomPlatform, setShowCustomPlatform] = useState(
    !SUGGESTED_PLATFORMS.includes(source.platform) && !!source.platform
  );

  const lastSync = source.last_synced_at
    ? new Date(source.last_synced_at).toLocaleString("fr-FR", {
        dateStyle: "short",
        timeStyle: "short",
      })
    : null;

  return (
    <div
      data-testid={`ical-source-${source.id}`}
      className="rounded-xl border border-white/10 bg-white/[0.04] p-4 space-y-3"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-slate-300 text-xs">Plateforme</Label>
          {showCustomPlatform ? (
            <Input
              data-testid={`ical-source-${source.id}-platform-custom`}
              value={source.platform}
              onChange={(e) => onChange({ ...source, platform: e.target.value })}
              placeholder="Nom de la plateforme"
              className="mt-1 border-white/10 bg-white/[0.08] text-slate-100"
            />
          ) : (
            <select
              data-testid={`ical-source-${source.id}-platform`}
              value={source.platform || ""}
              onChange={(e) => {
                if (e.target.value === "Autre") {
                  setShowCustomPlatform(true);
                  onChange({ ...source, platform: "" });
                } else {
                  onChange({ ...source, platform: e.target.value });
                }
              }}
              className="mt-1 flex h-9 w-full rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 text-sm text-slate-100"
            >
              <option value="">— Choisir —</option>
              {SUGGESTED_PLATFORMS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          )}
        </div>
        <div>
          <Label className="text-slate-300 text-xs">Logement</Label>
          <select
            data-testid={`ical-source-${source.id}-property`}
            value={source.property_id || ""}
            onChange={(e) => onChange({ ...source, property_id: e.target.value })}
            className="mt-1 flex h-9 w-full rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 text-sm text-slate-100"
          >
            <option value="">— Choisir —</option>
            {properties.map((p) => (
              <option key={p.id} value={p.id}>
                {p.short_name || p.residence_name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <Label className="text-slate-300 text-xs">URL iCal (https://...)</Label>
        <Input
          data-testid={`ical-source-${source.id}-url`}
          value={source.url || ""}
          onChange={(e) => onChange({ ...source, url: e.target.value })}
          placeholder="https://www.airbnb.com/calendar/ical/..."
          className="mt-1 border-white/10 bg-white/[0.08] text-slate-100"
        />
      </div>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Switch
            data-testid={`ical-source-${source.id}-active`}
            checked={!!source.active}
            onCheckedChange={(v) => onChange({ ...source, active: !!v })}
          />
          <span className="text-sm text-slate-300">{source.active ? "Actif" : "Désactivé"}</span>
        </div>
        <div className="flex items-start gap-2 text-xs">
          {source.last_sync_error ? (
            <div data-testid={`ical-source-${source.id}-error`} className="flex flex-1 items-start gap-1.5 rounded-md border border-rose-400/30 bg-rose-500/10 px-2.5 py-2 text-rose-100">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-rose-300" />
              <span className="leading-snug">{explainSyncError(source.last_sync_error)}</span>
            </div>
          ) : lastSync ? (
            <span className="text-emerald-300 flex items-center gap-1">
              <CheckCircle2 className="h-3.5 w-3.5" /> Dernière sync : {lastSync} ({source.last_sync_count || 0}{" "}
              réservation{(source.last_sync_count || 0) > 1 ? "s" : ""} importée{(source.last_sync_count || 0) > 1 ? "s" : ""}
              {source.last_sync_total != null && source.last_sync_total > (source.last_sync_count || 0) ? ` sur ${source.last_sync_total}` : ""})
            </span>
          ) : (
            <span className="text-slate-500">Jamais synchronisé</span>
          )}
          <Button
            data-testid={`ical-source-${source.id}-remove`}
            size="sm"
            variant="outline"
            onClick={onRemove}
            className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function OwnerCalendars() {
  const [sources, setSources] = useState([]);
  const [defaults, setDefaults] = useState({
    kit_linge_simple: 0,
    kit_linge_double: 0,
    lits_faits: false,
    kit_hygiene: false,
    auto_apply_defaults: false,
  });
  const [properties, setProperties] = useState([]);
  const [savingSources, setSavingSources] = useState(false);
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, d, p] = await Promise.all([
        api.get("/owner/ical-sources"),
        api.get("/owner/default-services"),
        api.get("/owner/properties"),
      ]);
      setSources(s.data?.sources || []);
      setDefaults(d.data || {});
      setProperties(p.data || []);
    } catch (e) {
      toast({ title: "Erreur de chargement", description: formatError(e), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const canSaveSources = useMemo(
    () => sources.every((s) => s.platform?.trim() && s.url?.trim() && s.property_id),
    [sources]
  );

  const addSource = () => {
    if (!properties.length) {
      toast({ title: "Aucun logement", description: "Créez d'abord un logement.", variant: "destructive" });
      return;
    }
    setSources((prev) => [
      ...prev,
      {
        id: genId(),
        platform: "",
        url: "",
        property_id: properties[0]?.id || "",
        active: true,
        last_synced_at: null,
        last_sync_error: null,
        last_sync_count: 0,
      },
    ]);
  };

  const updateSource = (id, next) => {
    setSources((prev) => prev.map((s) => (s.id === id ? next : s)));
  };

  const removeSource = (id) => {
    setSources((prev) => prev.filter((s) => s.id !== id));
  };

  const saveSources = async () => {
    if (!canSaveSources) {
      toast({
        title: "Champs incomplets",
        description: "Chaque source iCal doit avoir une plateforme, une URL et un logement.",
        variant: "destructive",
      });
      return;
    }
    try {
      setSavingSources(true);
      const res = await api.put("/owner/ical-sources", { sources });
      setSources(res.data?.sources || []);
      toast({ title: "✅ Calendriers enregistrés", description: `${(res.data?.sources || []).length} source(s) configurée(s)` });
    } catch (e) {
      toast({ title: "Erreur", description: formatError(e), variant: "destructive" });
    } finally {
      setSavingSources(false);
    }
  };

  const syncNow = async () => {
    try {
      setSyncing(true);
      const res = await api.post("/owner/ical-sources/sync");
      const r = res.data?.report || {};
      toast({
        title: "🔄 Synchronisation terminée",
        description: `${r.new || 0} nouvelle(s) réservation(s) — ${r.sources || 0} calendrier(s) lus`,
      });
      await load();
    } catch (e) {
      toast({ title: "Erreur de synchronisation", description: formatError(e), variant: "destructive" });
    } finally {
      setSyncing(false);
    }
  };

  const saveDefaults = async () => {
    try {
      setSavingDefaults(true);
      await api.put("/owner/default-services", defaults);
      toast({ title: "✅ Services par défaut enregistrés" });
    } catch (e) {
      toast({ title: "Erreur", description: formatError(e), variant: "destructive" });
    } finally {
      setSavingDefaults(false);
    }
  };

  if (loading) {
    return <div className="text-slate-400 py-10 text-center">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Section iCal */}
      <Card data-testid="ical-sources-card" className="border-white/10 bg-white/[0.08]">
        <CardHeader>
          <CardTitle className="text-slate-100 flex items-center gap-2">
            <CalendarRange className="h-5 w-5 text-violet-300" /> Mes calendriers iCal
          </CardTitle>
          <div className="text-sm text-slate-300">
            Connectez vos calendriers Airbnb, Booking, Abritel… BDM détectera automatiquement vos nouvelles réservations
            une fois par jour et vous enverra un email pour compléter les informations du voyageur.
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {sources.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/15 p-6 text-center text-slate-400">
              Aucun calendrier configuré. Cliquez sur <strong>« Ajouter un calendrier »</strong> pour commencer.
            </div>
          ) : (
            <div className="space-y-3">
              {sources.map((s) => (
                <IcalSourceRow
                  key={s.id}
                  source={s}
                  properties={properties}
                  onChange={(next) => updateSource(s.id, next)}
                  onRemove={() => removeSource(s.id)}
                />
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2">
            <Button data-testid="ical-add-btn" variant="outline" onClick={addSource} className="border-white/10 bg-white/[0.05] text-slate-100 hover:bg-white/10">
              <PlusCircle className="h-4 w-4 mr-2" /> Ajouter un calendrier
            </Button>
            <Button
              data-testid="ical-save-btn"
              onClick={saveSources}
              disabled={savingSources}
              className="bg-violet-500 text-white hover:bg-violet-400"
            >
              {savingSources ? "Sauvegarde..." : "Sauvegarder"}
            </Button>
            <Button
              data-testid="ical-sync-now-btn"
              onClick={syncNow}
              disabled={syncing || sources.length === 0}
              variant="outline"
              className="border-emerald-500/30 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Synchronisation..." : "Synchroniser maintenant"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Section services par défaut */}
      <Card data-testid="default-services-card" className="border-white/10 bg-white/[0.08]">
        <CardHeader>
          <CardTitle className="text-slate-100 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-fuchsia-300" /> Services par défaut
          </CardTitle>
          <div className="text-sm text-slate-300">
            Définissez les prestations à appliquer automatiquement aux nouvelles réservations iCal détectées.
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300 text-sm">🛏️ Kit linge simple (1 pers.)</Label>
              <Input
                data-testid="defaults-kit-simple"
                type="number"
                min="0"
                value={defaults.kit_linge_simple}
                onChange={(e) =>
                  setDefaults({ ...defaults, kit_linge_simple: Math.max(0, parseInt(e.target.value, 10) || 0) })
                }
                className="mt-1 border-white/10 bg-white/[0.08] text-slate-100"
              />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">🛏️ Kit linge double (2 pers.)</Label>
              <Input
                data-testid="defaults-kit-double"
                type="number"
                min="0"
                value={defaults.kit_linge_double}
                onChange={(e) =>
                  setDefaults({ ...defaults, kit_linge_double: Math.max(0, parseInt(e.target.value, 10) || 0) })
                }
                className="mt-1 border-white/10 bg-white/[0.08] text-slate-100"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] p-3 cursor-pointer">
              <Checkbox
                data-testid="defaults-lits-faits"
                checked={!!defaults.lits_faits}
                onCheckedChange={(v) => setDefaults({ ...defaults, lits_faits: !!v })}
                disabled={!(defaults.kit_linge_simple > 0 || defaults.kit_linge_double > 0)}
              />
              <span className="text-sm text-slate-100">
                ✅ Lits faits
                {!(defaults.kit_linge_simple > 0 || defaults.kit_linge_double > 0) && (
                  <span className="ml-2 text-xs text-amber-300">(verrouillé sans kit linge)</span>
                )}
              </span>
            </label>
            <label className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] p-3 cursor-pointer">
              <Checkbox
                data-testid="defaults-kit-hygiene"
                checked={!!defaults.kit_hygiene}
                onCheckedChange={(v) => setDefaults({ ...defaults, kit_hygiene: !!v })}
              />
              <span className="text-sm text-slate-100">🧴 Kit hygiène</span>
            </label>
          </div>

          <div className="rounded-xl border border-violet-500/30 bg-violet-500/10 p-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                data-testid="defaults-auto-apply"
                checked={!!defaults.auto_apply_defaults}
                onCheckedChange={(v) => setDefaults({ ...defaults, auto_apply_defaults: !!v })}
              />
              <div>
                <div className="text-sm font-medium text-violet-100">
                  Appliquer automatiquement ces services à chaque réservation iCal détectée
                </div>
                <div className="text-xs text-violet-200/80 mt-0.5">
                  Si désactivé, chaque réservation arrive en mode &laquo;&nbsp;à compléter&nbsp;&raquo; sans service présélectionné.
                </div>
              </div>
            </label>
          </div>

          <div className="flex justify-end">
            <Button
              data-testid="defaults-save-btn"
              onClick={saveDefaults}
              disabled={savingDefaults}
              className="bg-fuchsia-500 text-white hover:bg-fuchsia-400"
            >
              {savingDefaults ? "Sauvegarde..." : "Sauvegarder les services"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
